import { Link } from "react-router-dom";
import { ArrowRight, Award, BookOpen, Leaf, Baby, Eye, Puzzle, HeartHandshake, Home, Users } from "lucide-react";

const PORTRAIT = "https://customer-assets-m6fa6gv7.emergentagent.net/job_lea-parentalite/artifacts/hlj2n8ev_image.png";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Intro */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-28">
        <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-6 fade-up">À propos de Léa</p>
        <h1 data-testid="about-title" className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#4A403A] leading-[1.05] mb-10 fade-up fade-up-delay-1">
          Accompagner sans juger,<br/>
          <em className="text-[#BA8E82]">avec douceur et méthode</em>.
        </h1>
        <div className="grid lg:grid-cols-12 gap-12 items-start mt-12">
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden shadow-soft aspect-[4/5] fade-up fade-up-delay-2">
              <img src={PORTRAIT} alt="Léa en séance de conseil parental à domicile avec une famille" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg text-[#8B7E74] leading-relaxed lg:pl-6">
            <p>
              Je m'appelle Léa, et depuis 5 ans, j'accompagne les jeunes enfants et leurs
              familles. Je suis titulaire du <strong className="text-[#4A403A] font-medium">Diplôme d'État d'Éducatrice de Jeunes Enfants (EJE)</strong> :
              une formation de trois ans, en psychologie, pédagogie et développement du jeune enfant.
            </p>
            <p>
              <strong className="text-[#4A403A] font-medium">Le métier d'EJE</strong> consiste à
              favoriser le développement, l'éveil et le bien-être des enfants de 0 à 7 ans,
              en s'appuyant sur l'observation, le jeu et la relation. C'est un métier du «&nbsp;lien&nbsp;» :
              comprendre l'enfant, soutenir ses parents et créer les conditions d'un quotidien serein.
            </p>
            <p>
              En créant Léa Parentalité, j'ai voulu offrir un espace différent : un accompagnement
              <em> à votre rythme</em>, dans <em>votre quotidien</em>, là où les choses se vivent vraiment — à la maison.
            </p>
            <p>
              Ma conviction profonde&nbsp;: chaque parent a déjà en lui des ressources extraordinaires.
              Mon rôle est de vous aider à les révéler, à poser des mots sur les émotions et à
              construire ensemble des repères qui vous ressemblent.
            </p>
            <p className="pt-2 text-[#4A403A]/80 italic border-l-2 border-[#D2A69A] pl-5">
              Je suis moi-même concernée par le HPI et le TDAH, ce qui nourrit une sensibilité
              particulière et une écoute concrète pour les enfants et les familles qui vivent
              cette réalité.
            </p>
          </div>
        </div>
      </section>

      {/* Pitch / Ma mission */}
      <section className="bg-[#4A403A]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24 md:py-28 text-center">
          <p className="text-xs tracking-[0.25em] uppercase text-[#D2A69A] mb-8">Ma mission</p>
          <blockquote data-testid="about-pitch" className="font-serif text-2xl md:text-3xl text-[#FAF7F2] leading-relaxed italic">
            « J'accompagne à domicile les familles ayant des enfants de 0 à 6 ans qui traversent une
            période difficile : les colères, les "non", les émotions, les changements de vie ou
            simplement un quotidien devenu compliqué. Mon objectif est d'intervenir avant que la
            situation ne nécessite un suivi médical ou psychologique.
            <br/><br/>
            J'offre un espace d'écoute bienveillant, sans jugement, où l'on cherche ensemble des
            solutions concrètes. J'observe l'enfant dans son environnement, je conseille les parents
            et je peux même les accompagner dans la mise en place des actions pour qu'ils ne se
            sentent plus seuls.
            <br/><br/>
            <span className="text-[#D2A69A] not-italic">Ma mission : redonner aux familles des repères, de la confiance et un quotidien plus serein. »</span>
          </blockquote>
        </div>
      </section>

      {/* Expertise, formations & compétences (regroupé) */}
      <section className="bg-[#F4EBE6]/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-28">
          <div className="max-w-3xl mb-14">
            <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-5">Expertise & compétences</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A403A] leading-tight">
              Ce qui compose<br/><em className="text-[#BA8E82]">mon savoir-faire</em>.
            </h2>
            <p className="mt-6 text-[#8B7E74] leading-relaxed text-lg">
              L'expertise d'une Éducatrice de Jeunes Enfants s'articule autour de plusieurs domaines
              complémentaires, enrichis par mes formations et mon expérience de terrain.
            </p>
          </div>

          {/* Les volets — version condensée */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 mb-14">
            {[
              { icon: Baby, title: "Développement global", text: "Moteur, langagier, affectif, cognitif et social." },
              { icon: Eye, title: "Observation", text: "Décoder les besoins, émotions et comportements." },
              { icon: Puzzle, title: "Jeu & éveil", text: "Découverte, créativité et autonomie par le jeu." },
              { icon: HeartHandshake, title: "Soutien à la parentalité", text: "Écouter, conseiller et guider sans jugement." },
              { icon: Home, title: "Aménagement de l'espace", text: "Un environnement à hauteur d'enfant." },
              { icon: Users, title: "Lien & médiation", text: "Poser des repères durables et rassurants." },
            ].map((v, i) => (
              <div key={v.title} data-testid={`expertise-card-${i}`} className="flex gap-4 items-start">
                <span className="inline-flex w-11 h-11 rounded-2xl bg-white border border-[#E5DCD6] items-center justify-center text-[#BA8E82] shrink-0">
                  <v.icon size={18} strokeWidth={1.6} />
                </span>
                <div>
                  <h3 className="font-serif text-xl text-[#4A403A] leading-tight">{v.title}</h3>
                  <p className="text-[#8B7E74] text-sm leading-relaxed mt-1">{v.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Formations */}
          <div className="grid sm:grid-cols-3 gap-5">
            <div data-testid="training-eeje" className="bg-white rounded-3xl p-7 border border-[#E5DCD6] shadow-softer">
              <p className="text-xs tracking-[0.18em] uppercase text-[#BA8E82] bg-[#F4EBE6] rounded-full px-3 py-1 inline-block mb-4">Diplôme</p>
              <h3 className="font-serif text-2xl text-[#4A403A] mb-2">D.E.E.J.E</h3>
              <p className="text-[#8B7E74] text-sm leading-relaxed">Diplôme d'État d'Éducatrice de Jeunes Enfants — 3 ans en psychologie et pédagogie du jeune enfant.</p>
            </div>
            <div data-testid="training-espace" className="bg-white rounded-3xl p-7 border border-[#E5DCD6] shadow-softer">
              <p className="text-xs tracking-[0.18em] uppercase text-[#BA8E82] bg-[#F4EBE6] rounded-full px-3 py-1 inline-block mb-4">Formation · FM2J</p>
              <h3 className="font-serif text-2xl text-[#4A403A] mb-2">Aménagement de l'espace</h3>
              <p className="text-[#8B7E74] text-sm leading-relaxed">Formée avec le FM2J (centre national de formation aux métiers du jeu et du jouet), pour un environnement pensé pour l'autonomie et le bien-être de l'enfant.</p>
            </div>
            <div data-testid="training-hpi-tdah" className="bg-white rounded-3xl p-7 border border-[#E5DCD6] shadow-softer">
              <p className="text-xs tracking-[0.18em] uppercase text-[#BA8E82] bg-[#F4EBE6] rounded-full px-3 py-1 inline-block mb-4">En préparation · 2026</p>
              <h3 className="font-serif text-2xl text-[#4A403A] mb-2">Enfants HPI & TDAH</h3>
              <p className="text-[#8B7E74] text-sm leading-relaxed">Formation dédiée en préparation — un sujet qui me tient à cœur, <em className="text-[#4A403A]">étant moi-même concernée</em>.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Approche */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-28">
        <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-5">Mon approche</p>
        <h2 className="font-serif text-4xl md:text-5xl text-[#4A403A] leading-tight mb-14 max-w-3xl">
          Une démarche fondée sur l'observation, la bienveillance et le respect du rythme de chaque enfant.
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Award,
              title: "Diplômée d'État",
              text: "Diplôme d'Éducatrice de Jeunes Enfants (D.E.E.J.E), une formation de 3 ans en psychologie et pédagogie du jeune enfant.",
            },
            {
              icon: BookOpen,
              title: "Pédagogies vivantes",
              text: "Inspirée par Montessori, Pikler-Lóczy, la communication non violente et les neurosciences affectives.",
            },
            {
              icon: Leaf,
              title: "Approche douce",
              text: "Aucune injonction, aucune méthode rigide. Nous co-construisons des solutions adaptées à votre famille.",
            },
          ].map((p, i) => (
            <div
              key={p.title}
              data-testid={`approach-card-${i}`}
              className="bg-white rounded-3xl p-8 border border-[#E5DCD6] shadow-softer"
            >
              <span className="inline-flex w-12 h-12 rounded-full bg-[#F4EBE6] items-center justify-center text-[#BA8E82] mb-6">
                <p.icon size={20} strokeWidth={1.6} />
              </span>
              <h3 className="font-serif text-2xl text-[#4A403A] mb-3">{p.title}</h3>
              <p className="text-[#8B7E74] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pour qui — déplacé vers la page Services & Tarifs */}

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-32 pt-24">
        <div className="bg-[#4A403A] rounded-[2rem] px-10 md:px-16 py-16 md:py-20 text-center">
          <h3 className="font-serif text-3xl md:text-4xl text-[#FAF7F2] mb-5">
            Et si nous faisions connaissance&nbsp;?
          </h3>
          <p className="text-[#FAF7F2]/80 mb-3 max-w-xl mx-auto">
            Un <strong className="text-[#D2A69A] font-medium">1<sup>er</sup> appel gratuit</strong> pour faire connaissance,
            puis un <strong className="text-[#D2A69A] font-medium">Bilan familial (1h · 50€)</strong> pour
            échanger, observer et imaginer ensemble la suite — sans engagement.
          </p>
          <p className="text-[#FAF7F2]/60 text-sm mb-9 max-w-xl mx-auto">
            À domicile ou en visio, à votre rythme.
          </p>
          <Link
            to="/rendez-vous"
            data-testid="about-cta-booking"
            className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 bg-[#D2A69A] text-[#4A403A] text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#FAF7F2]"
          >
            Prendre rendez-vous <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
